import { preloadModule } from 'react-dom'
import catalogsByChunk from 'virtual:i18n-catalogs'
import type { AnyRouter } from '@tanstack/react-router'
import type { Messages } from '@lingui/core'
import { locales } from 'shared/locales'

/**
 * The browser half of the route catalogs (vite-plugin-route-catalogs.ts). Every built chunk that owns catalogs opens
 * with `await __i18n.chunk(import.meta.url, keys, inline?)`, so this runs inline in the head, before any chunk. A key
 * shared by several chunks (the form kit's) is fetched once per locale. ONE messages object per locale, handed to
 * Lingui by reference: a late catalog merges into what Lingui already reads, with no `change` event re-rendering every
 * translated node. `url` feeds Vite's preload list, which is built once, so its string form is read per preload and
 * follows a switch. ES5, byte-identical on both sides. docs/notes/app/i18n-catalogs.md
 */
export const CATALOG_BOOTSTRAP_SCRIPT =
  'globalThis.__i18n=globalThis.__i18n||(function(){' +
  `var known=${JSON.stringify(locales)},sources={},messages={},locale=document.documentElement.lang;` +
  'function file(key,l){return "./i18n-"+key+"."+l+".js"}' +
  'function fetchAll(l,list){var m=messages[l]||(messages[l]={});' +
  'return Promise.all(known.indexOf(l)<0?[]:list.map(function(get){' +
  'return get(l).then(function(c){Object.assign(m,c)})})).then(function(){return m})}' +
  'function all(){var list=[];for(var k in sources)list.push(sources[k]);return list}' +
  'return{chunk:function(base,keys,inline){var list=keys.map(function(key){' +
  'return sources[key]||(sources[key]=function(l){return import(new URL(file(key,l),base).href).then(function(c){return c.default})})});' +
  'if(inline)list.push(sources[base]=function(l){return Promise.resolve(inline[l]||{})});' +
  'return fetchAll(locale,list)},' +
  'load:function(l){locale=l;return fetchAll(l,all())},' +
  'fetch:function(l){return fetchAll(l,all())},' +
  'url:function(key){return{toString:function(){return file(key,known.indexOf(locale)<0?known[0]:locale)}}}}})();'

declare global {
  var __i18n: {
    /** Every catalog of every chunk loaded so far, in `locale`; resolves to that locale's (shared) messages object. */
    load: (locale: string) => Promise<Messages>
    /** As `load`, without switching: the page keeps its locale, and later chunks still fetch only that one. */
    fetch: (locale: string) => Promise<Messages>
  }
}

/** Production client only: a Lingui catalog assembled from the chunks loaded so far, in `locale`. */
export const loadChunkCatalogs = (locale: string): Promise<Messages> => globalThis.__i18n.load(locale)

/**
 * SSR only: hint the catalogs of every chunk the matched routes preload, in this request's locale, so they download
 * with the chunks instead of after them. `preloadModule` is a resource hint, not an element, so the server and client
 * trees stay identical.
 */
export function preloadRouteCatalogs(router: AnyRouter, locale: string): void {
  const manifest = router.ssr?.manifest
  if (!manifest) return
  const seen = new Set<string>()
  for (const match of router.state.matches) {
    for (const preload of manifest.routes[match.routeId]?.preloads ?? []) {
      const href = typeof preload === 'string' ? preload : preload.href
      const slash = href.lastIndexOf('/') + 1
      for (const key of catalogsByChunk[href.slice(slash)] ?? []) {
        if (seen.has(key)) continue
        seen.add(key)
        const url = `${href.slice(0, slash)}i18n-${key}.${locale}.js`
        preloadModule(url, url.startsWith('http') ? { as: 'script', crossOrigin: 'anonymous' } : { as: 'script' })
      }
    }
  }
}
